import { useEffect, useRef, useState } from 'react';
import { useLoadingState } from '../hooks';
import { TwindenLoader } from '../components/brand/TwindenLoader';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  HStack,
  Text,
  VStack,
  useMediaQuery,
} from '@chakra-ui/react';
import { Check, KeyRound, ShieldAlert } from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import { WS_EVENTS, type QrDeviceKind } from '@twinden/shared';
import { useAuth } from '../hooks/useAuth';
import { WebSocketProvider, useWebSocket } from '../hooks/useWebSocket';
import { api, ApiError } from '../lib/api';
import { shareKeys, startE2EE } from '../lib/e2ee/session';
import { appLocale } from '../lib/format';
import { WelcomeShell } from '../components/page/WelcomeShell';
import { ContinueAs } from '../components/family/JoinChoice';
import { signInErrorMessage } from './LoginPage';
import { useT, type MessageKey } from '../i18n';

// The phone's side of sign in by scanning, /link/:code (M49, D158, D159,
// D161). It sits outside the app shell, so it brings its own socket for the
// key handover.

/**
 * The longest the phone shows "Sending your messages to the computer…"
 * (D161, #74): waiting for the computer's device key and handing the keys
 * over together. Then it says they'll follow in a moment; a share under way
 * keeps going, and a late `keys:needed` is still answered while this page
 * is open. The computer's own asks (on connect, ~40 s and ~2 min later) and
 * the app's share on every `keys:needed` cover anything left (D57).
 */
export const HANDOVER_LIMIT_MS = 20 * 1000;

type Info = {
  deviceKind: QrDeviceKind;
  createdAt: string;
  choices: number[];
};

type Step =
  | { name: 'signIn' }
  | { name: 'continueAs' }
  | { name: 'loading' }
  | { name: 'confirm'; info: Info }
  | { name: 'handover' }
  // `keys`: whether the handover reached the computer (D161). `doneSoon`:
  // the 20 s limit came first and the keys follow (#74).
  | { name: 'done'; keys: boolean }
  | { name: 'doneSoon' }
  | { name: 'ended'; message: MessageKey };

export function LinkSignInPage() {
  return (
    <WebSocketProvider>
      <LinkSignIn />
    </WebSocketProvider>
  );
}

function endedFor(err: unknown): MessageKey | null {
  if (!(err instanceof ApiError)) return null;
  if (err.code === 'WRONG_NUMBER') return 'account.scanApprove.wrongNumber';
  if (err.code === 'CODE_EXPIRED') return 'account.scanApprove.expired';
  return null;
}

function LinkSignIn() {
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const t = useT();
  const { currentUser, isAuthenticated, logout, signInWithPasskey } = useAuth();
  // A computer is the one being signed in, not the scanner (D158).
  const [narrow] = useMediaQuery('(max-width: 767px)', { ssr: false });
  const touch =
    typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
  const isPhone = narrow || touch;

  const [chosen, setStep] = useState<Step>(
    isAuthenticated ? { name: 'continueAs' } : { name: 'signIn' }
  );
  // Signed out meanwhile (another tab, an expired session): sign in again.
  const step: Step =
    !currentUser && ['continueAs', 'loading', 'confirm'].includes(chosen.name)
      ? { name: 'signIn' }
      : chosen;
  const [busy, setBusy] = useState(false);
  // The mark in the shell while the server answers (M66, D248).
  const shellWait = useLoadingState({
    loading: step.name === 'loading' || step.name === 'handover',
  });
  const [picked, setPicked] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const e2ee = useRef<Promise<void> | null>(null);

  const userId = currentUser?.id;

  // Look the code up once we know who is approving it (Review Focus 5).
  useEffect(() => {
    if (step.name !== 'loading' || !userId) return;
    let cancelled = false;
    // This browser's own device key, so it can hand keys over (D161).
    e2ee.current = startE2EE(userId);
    api
      .getQrSignIn(code)
      .then((res) => {
        if (!cancelled) setStep({ name: 'confirm', info: res.data });
      })
      .catch((err) => {
        if (cancelled) return;
        setStep({
          name: 'ended',
          message: endedFor(err) ?? 'account.scanApprove.loadFailed',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [step.name, userId, code]);

  // D161: wait for the computer's device key, then hand it every key here
  // (the D138 handover). Only events from the moment we approve count: the
  // ref is set just before the approval goes out, so a fast computer's
  // KEYS_NEEDED isn't missed while the approval's answer is on its way.
  const { onEvent } = useWebSocket();
  const waiting = step.name === 'handover';
  const waitingRef = useRef(false);
  // After the limit (#74) the screen has moved on, but a computer that asks
  // late still gets the keys while this page is open.
  const lateRef = useRef(false);
  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (event !== WS_EVENTS.KEYS_NEEDED) return;
        if ((payload as { userId?: string }).userId !== userId) return;
        if (lateRef.current) {
          void shareKeys().catch(() => undefined);
          return;
        }
        if (!waitingRef.current) return;
        waitingRef.current = false;
        setStep({ name: 'handover' });
        void shareKeys()
          .catch(() => ({ chats: 0, devices: 0 }))
          .then((shared) =>
            // Nothing handed over (this browser holds no keys, or the
            // computer got none): don't claim it has the messages. The limit
            // may have moved the screen on already; it stays there.
            setStep((current) =>
              current.name === 'handover'
                ? { name: 'done', keys: shared.devices > 0 && shared.chats > 0 }
                : current
            )
          );
      }),
    [onEvent, userId]
  );
  // The hard limit on "Sending…" (D161, #74): whether the computer never
  // asks (it kept no new key: a browser that can't, D262, or one it had
  // already) or the share is slow, the phone moves on.
  useEffect(() => {
    if (!waiting) return;
    const timer = window.setTimeout(() => {
      lateRef.current = waitingRef.current;
      waitingRef.current = false;
      setStep((current) =>
        current.name === 'handover' ? { name: 'doneSoon' } : current
      );
    }, HANDOVER_LIMIT_MS);
    return () => window.clearTimeout(timer);
  }, [waiting]);

  const signIn = async () => {
    setError(null);
    setBusy(true);
    try {
      await signInWithPasskey();
      setStep({ name: 'loading' });
    } catch (err) {
      setError(signInErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    await logout().catch(() => undefined);
    setError(null);
    setStep({ name: 'signIn' });
  };

  // D159 (owner's choice B): the number first, then the passkey check; the
  // server checks the passkey before it spends the one try.
  const choose = async (number: number) => {
    setError(null);
    setPicked(number);
    try {
      const options = await api.qrSignInVerifyStart(code);
      const verification = await startAuthentication({
        optionsJSON:
          options.data as unknown as PublicKeyCredentialRequestOptionsJSON,
      });
      await e2ee.current;
      waitingRef.current = true;
      await api.approveQrSignIn(code, number, verification);
      // Unless the computer already asked for keys meanwhile.
      if (waitingRef.current) setStep({ name: 'handover' });
    } catch (err) {
      waitingRef.current = false;
      const ended = endedFor(err);
      if (ended) setStep({ name: 'ended', message: ended });
      else setError(signInErrorMessage(err));
    } finally {
      setPicked(null);
    }
  };

  const cancel = async () => {
    await api.cancelQrSignIn(code).catch(() => undefined);
    setStep({ name: 'ended', message: 'account.scanApprove.cancelled' });
  };

  const openTwinden = (
    <Button
      h="56px"
      fontSize="17px"
      w="full"
      onClick={() => navigate('/', { replace: true })}
    >
      {t('account.scanApprove.openTwinden')}
    </Button>
  );

  if (!isPhone) {
    return (
      <WelcomeShell
        title={t('account.scanApprove.openOnPhone')}
        subtitle={t('account.scanApprove.openOnPhoneBody')}
      >
        {null}
      </WelcomeShell>
    );
  }

  const errorAlert = error && (
    <Alert status="error" borderRadius="xl">
      <AlertIcon />
      {error}
    </Alert>
  );

  switch (step.name) {
    case 'signIn':
      return (
        <WelcomeShell
          title={t('account.scanApprove.heading')}
          subtitle={t('account.scanApprove.signInFirst')}
        >
          <VStack spacing={3} align="stretch">
            {errorAlert}
            <Button
              h="56px"
              fontSize="17px"
              leftIcon={<KeyRound size={20} />}
              onClick={signIn}
              isLoading={busy}
            >
              {t('account.scanApprove.signIn')}
            </Button>
          </VStack>
        </WelcomeShell>
      );

    case 'continueAs':
      return (
        <WelcomeShell title={t('account.scanApprove.heading')}>
          <ContinueAs
            name={currentUser?.displayName ?? ''}
            onContinue={() => setStep({ name: 'loading' })}
            onSignOut={signOut}
          />
        </WelcomeShell>
      );

    case 'loading':
      return (
        <WelcomeShell title={t('account.scanApprove.heading')}>
          <Center>{shellWait.loader && <TwindenLoader />}</Center>
        </WelcomeShell>
      );

    case 'confirm': {
      const { info } = step;
      const time = new Date(info.createdAt).toLocaleTimeString(appLocale(), {
        hour: 'numeric',
        minute: '2-digit',
      });
      return (
        <WelcomeShell title={t('account.scanApprove.title')}>
          <VStack spacing={5} align="stretch">
            <Box bg="surface" borderRadius="xl" p={4}>
              {info.deviceKind !== 'other' ? (
                <>
                  <Text color="muted" fontSize="sm">
                    {t('account.scanApprove.says')}
                  </Text>
                  {/* Named in our own words from a fixed list (D159): a hint,
                      not a promise, and never text the server passes on. */}
                  <Text fontWeight={700} fontSize="18px">
                    {t(`account.scanApprove.kind.${info.deviceKind}`)}
                  </Text>
                </>
              ) : (
                <Text color="muted">
                  {t('account.scanApprove.saysNothing')}
                </Text>
              )}
              <Text color="muted" fontSize="sm" mt={1}>
                {t('account.scanApprove.madeAt', { time })}
              </Text>
            </Box>
            <HStack align="start" spacing={3} color="danger">
              <Box pt="2px" flexShrink={0}>
                <ShieldAlert size={20} aria-hidden="true" />
              </Box>
              <Text fontWeight={600}>{t('account.scanApprove.warning')}</Text>
            </HStack>
            {errorAlert}
            <VStack spacing={2} align="stretch">
              <Text fontWeight={700} fontSize="18px">
                {t('account.scanApprove.whichNumber')}
              </Text>
              <Text color="muted" fontSize="sm">
                {t('account.scanApprove.numberHint')}
              </Text>
              <HStack spacing={3} pt={1}>
                {info.choices.map((number) => (
                  <Button
                    key={number}
                    flex={1}
                    h="72px"
                    fontSize="28px"
                    variant="outline"
                    colorScheme="gray"
                    onClick={() => void choose(number)}
                    isLoading={picked === number}
                    isDisabled={picked !== null && picked !== number}
                  >
                    {number}
                  </Button>
                ))}
              </HStack>
            </VStack>
            <Button
              variant="ghost"
              colorScheme="gray"
              onClick={() => void cancel()}
              isDisabled={picked !== null}
            >
              {t('account.scanApprove.cancel')}
            </Button>
          </VStack>
        </WelcomeShell>
      );
    }

    case 'handover':
      return (
        <WelcomeShell
          title={t('account.scanApprove.sending')}
          subtitle={t('account.scanApprove.sendingHint')}
        >
          <Center>{shellWait.loader && <TwindenLoader />}</Center>
        </WelcomeShell>
      );

    case 'done':
    case 'doneSoon':
      return (
        <WelcomeShell
          title={t('account.scanApprove.done')}
          subtitle={t(
            step.name === 'doneSoon'
              ? 'account.scanApprove.doneSoon'
              : step.keys
                ? 'account.scanApprove.doneHint'
                : 'account.scanApprove.doneNoKeys'
          )}
        >
          <VStack spacing={4}>
            <Center
              w="56px"
              h="56px"
              borderRadius="full"
              bg="accent.tint"
              color="accent"
            >
              <Check size={28} />
            </Center>
            {openTwinden}
          </VStack>
        </WelcomeShell>
      );

    case 'ended':
      return (
        <WelcomeShell title={t('account.scanApprove.heading')}>
          <VStack spacing={4} align="stretch">
            <Alert status="warning" borderRadius="xl">
              <AlertIcon />
              {t(step.message)}
            </Alert>
            {openTwinden}
          </VStack>
        </WelcomeShell>
      );
  }
}
